import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { aiResultsApi, AiResultKind, SaveAiResultBody } from '@/lib/ai-results';
import { readApiError } from '@/lib/auth';

/**
 * Drop-in "Save" button for an agent result. Persists the output to the
 * workspace's AI Results library so it can be reopened later (instead of being
 * lost on navigation). Shows Save → Saved ✓ and surfaces errors inline.
 */
export function SaveResultButton(props: {
  workspaceId: string;
  kind: AiResultKind;
  title: string;
  output: string;
  input?: Record<string, unknown>;
  citedChunkIds?: (string | number)[];
  model?: string;
  costCents?: number;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: SaveAiResultBody = {
        kind: props.kind,
        title: props.title.trim().slice(0, 200) || t('ai_results.untitled', { defaultValue: 'Untitled result' }) as string,
        output: props.output,
        input: props.input,
        citedChunkIds: props.citedChunkIds,
        model: props.model,
        costCents: props.costCents,
      };
      return aiResultsApi.save(props.workspaceId, body);
    },
    onSuccess: () => {
      setSaved(true);
      setError(null);
      window.setTimeout(() => setSaved(false), 2400);
    },
    onError: (err) => setError(readApiError(err, 'Could not save.')),
  });

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={props.disabled || save.isPending || !props.output?.trim()}
        className="btn-ghost px-3 py-1.5 text-sm disabled:opacity-50"
        title={t('ai_results.save_hint', { defaultValue: 'Save to your AI Results library' }) as string}
      >
        {save.isPending
          ? t('common.saving', { defaultValue: 'Saving…' })
          : saved
            ? t('ai_results.saved', { defaultValue: 'Saved ✓' })
            : t('ai_results.save', { defaultValue: 'Save result' })}
      </button>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  );
}
